import React, { useState } from "react";

import Info from "@hl/base-components/lib/assets/icons.generated/Info";
import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { STATUS_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import {
  Button,
  Group,
  Stack,
  Text,
  Card,
  Modal,
  MantineSize,
  UnstyledButton,
  Flex,
  useMantineTheme,
  Divider,
} from "@mantine/core";
import { IconRefresh } from "@tabler/icons";

import { useRefreshCollectionMetadataMutation } from "~features/MintPage/refresh-metadata/queries.graphql.generated";
import useMintState from "~hooks/useMintState";

const RefreshCollectionMetadataButton = ({
  mt,
  compact,
}: {
  mt?: MantineSize | number;
  compact?: boolean;
}) => {
  const { collection, chainId, address } = useMintState();
  const theme = useMantineTheme();
  const enableRefreshCollectionMetadata = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_REFRESH_COLLECTION_METADATA
  );
  const [opened, setOpened] = useState(false);

  const [requested, setRequested] = useState(false);

  const [refreshCollectionMetadata, { loading, error }] =
    useRefreshCollectionMetadataMutation({
      onCompleted: () => setRequested(true),
      variables: {
        address,
        chainId,
        editionId: collection?.editionId || "0",
      },
    });

  if (!enableRefreshCollectionMetadata || !collection) {
    return <></>;
  }

  return (
    <Stack mt={mt}>
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title="Refresh metadata"
      >
        <Text size="sm" span>
          Please note that this process may consume{" "}
          <Text fw={WEIGHT_BOLD} size="sm" span>
            significant resources
          </Text>{" "}
          when the collection contains too many tokens.
        </Text>
        <ModalSection>
          <Group grow>
            <Button
              size="xl"
              color={SECONDARY_COLOR}
              onClick={() => setOpened(false)}
            >
              Cancel
            </Button>
            <Button
              size="xl"
              onClick={async () => {
                try {
                  await refreshCollectionMetadata();
                } finally {
                  setOpened(false);
                }
              }}
              loading={loading}
              disabled={requested}
            >
              Refresh metadata
            </Button>
          </Group>
        </ModalSection>
      </Modal>
      {requested && (
        <Card
          bg={STATUS_COLOR.INFORMATIVE_WASH}
          sx={[
            (theme) => ({
              border: `1px solid ${theme.fn.themeColor(
                STATUS_COLOR.INFORMATIVE
              )}`,
            }),
          ]}
        >
          <Group align="center" spacing="sm" noWrap>
            <Info height={24} width={24} />
            <Stack spacing={6}>
              <Text size="md" fw={WEIGHT_BOLD}>
                Metadata refresh requested
              </Text>
              <Text size="sm">
                Note that this process can take up to 1 hour to complete.
              </Text>
            </Stack>
          </Group>
        </Card>
      )}
      {compact ? (
        <Stack spacing={0}>
          <Divider my={14} />
          <UnstyledButton onClick={() => setOpened(true)} disabled={requested}>
            <Flex justify="space-between">
              <Text size="sm" color={theme.colors.errorStatus[0]}>
                Refresh metadata
              </Text>
              <IconRefresh color={theme.colors.errorStatus[0]} size={16} />
            </Flex>
          </UnstyledButton>
        </Stack>
      ) : (
        <Button
          fullWidth
          size="xl"
          onClick={() => setOpened(true)}
          disabled={requested}
        >
          Refresh metadata
        </Button>
      )}

      {error && (
        <Text size="sm" color={STATUS_COLOR.ERROR}>
          Error refreshing metadata. Try again.
        </Text>
      )}
    </Stack>
  );
};

export default RefreshCollectionMetadataButton;
